import dynamic from 'next/dynamic';
import { type ApolloCache, gql, type NormalizedCacheObject, useApolloClient, useQuery } from '@apollo/client';
import { useAuth } from '@zg-rentals/auth-comet';
import {
  AddAttachments,
  AddMessageLayout,
  CompactAddMessageLayout,
  hasPersonalContactDetails,
  MESSAGE_STATUS,
  PersonalContactDetailsWarningModal,
  TextareaAutoSize,
  TooltipWrapper,
  useMessageDrafts,
} from '@zg-rentals/satellite-components';
import { useUserWarnings } from '../../hooks/useUserWarnings';
import type React from 'react';
import { forwardRef, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type {
  AddMessage_AddMessageToConversationV2Mutation,
  MessageList_GetConversationQuery,
  MessageList_GetConversationQueryVariables,
  MutationZrm_AddMessageToConversationV2Args,
  Zrm_Attachment,
} from '../../types/GraphqlTypes';
import {
  MessageList_GetConversationDocument,
  useAddMessage_AddMessageToConversationV2Mutation,
} from '../../types/GraphqlTypes';
import {
  emitSendDraftMessageEvent,
  emitSendMessageEvent,
  SEND_MESSAGE_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/sendMessage';
import StringConstants from '../../constants/StringConstants';
import Urls from '../../constants/UrlConstants';
import {
  inboxPageAddMessageInputTextToAppendVariable,
  inboxPageClearAndAppendMessageInputTextVariable,
  inboxPageIsAddMessageInputFocusedVariable,
} from '../../lib/apollo';
import { AppContext } from '../../contexts/AppContextProvider';
import formValidationUtils from '../../utils/formValidationUtils';
import stringUtils from '../../utils/stringUtils';
import type { AttachmentsForMessage } from '../MessageAttachments';
import type { AttachmentsUploadedForAddMessage } from '@zg-rentals/satellite-components';
import UploadedAttachmentList from './components/UploadedAttachmentList';
import useFeatureFlagsHelper from '../../hooks/useFeatureFlagsHelper';
import QuickResponse from './components/QuickResponse';
import MessageListActions from '../MessageListActions';
import useModal from '../../hooks/useModal';
import { ModalType } from '../../types/ModalType';
import { logApolloError } from '../../utils/errorUtils';
import { appMarkerLogger } from '../../bootstrap';
import { Box } from '@zillow/constellation-10';
import { emitStoreMessageToLocalStorageEvent } from '../../constants/clickstreamEvents/conversationItem';
import { TRIGGER_LOCATION } from '../../constants/clickstreamEvents/globals';
import type { TriggerLocation } from '../../constants/clickstreamEvents/globals';
import {
  emitOpenPersonalInfoWarningModalEvent,
  emitSendAnywayInPersonalInfoWarningModalEvent,
} from '../../constants/clickstreamEvents/personalInfoWarningModal';

const AttachmentsUploader = dynamic(() => import('../AttachmentsUploader'), {
  loading: () => null,
  ssr: false,
});

const logger = appMarkerLogger.child({ name: 'AddMessage' });

export type SetAttachmentsUploadedForAddMessage = (uploadedAttachments: AttachmentsUploadedForAddMessage) => void;

type Props = {
  conversationId: string;
  listingAlias: string;
  clientTimeZone: string;
  fullMessageCountNumber: number;
  hasQuickResponses: boolean;
  quickResponses: MessageList_GetConversationQuery['ZRM_getConversationById']['quickResponses'];
  isConversationBlockedByZillow: boolean;
  isPhoneLead: boolean;
  isSpam: boolean;
  /** When 'compact', renders CompactAddMessageLayout with always-visible borders. */
  variant?: 'default' | 'compact';
  /** Trigger location for clickstream (inbox vs lead management). Defaults to inbox. */
  triggerLocation?: TriggerLocation;
  /** Optional callback invoked inside the mutation `update` after writing the new message to the message-list cache.
   *  Allows the parent to perform its own cache updates (e.g. conversation list reorder, renter profile attachments). */
  onAddMessageCompletedCacheUpdate?: (params: {
    cache: ApolloCache<NormalizedCacheObject>;
    newMessage: Partial<AddMessage_AddMessageToConversationV2Mutation['ZRM_addMessageToConversationV2']>;
    attachmentsForMessage: AttachmentsForMessage;
  }) => void;
};

/**
 * Message composer input for adding messages to a conversation.
 * Supports two variants:
 * - 'default': Full inbox layout with desktop/mobile views, tooltips, quick responses
 * - 'compact': Single-row compact layout with always-visible borders for modals
 */
const AddMessage = forwardRef<HTMLDivElement, Props>(
  (
    {
      conversationId,
      listingAlias,
      clientTimeZone,
      fullMessageCountNumber = 0,
      hasQuickResponses,
      quickResponses,
      isConversationBlockedByZillow,
      isPhoneLead,
      isSpam,
      variant = 'default',
      triggerLocation = TRIGGER_LOCATION.IreneInbox,
      onAddMessageCompletedCacheUpdate,
    }: Props,
    ref,
  ) => {
    const isCompact = variant === 'compact';
    const apolloClient = useApolloClient();
    const { isImpersonator, isMobileDevice } = useContext(AppContext);
    const [inputText, setInputText] = useState('');
    const { enableQuickResponse } = useFeatureFlagsHelper();
    const { user } = useAuth();

    const { data: addMessageData } = useQuery(GET_ADD_MESSAGE_INPUT_TEXT, { fetchPolicy: 'cache-only' });
    const {
      inboxPageAddMessageInputTextToAppend,
      inboxPageIsConversationSearchBarFocused,
      inboxPageIsAddMessageInputFocused,
      inboxPageClearAndAppendMessageInputText,
    } = addMessageData;
    const { personalContactWarningData } = useUserWarnings();

    const [currentTempMessageId, setCurrentTempMessageId] = useState(String(Date.now()));
    const [uploadedAttachments, setUploadedAttachments] = useState<AttachmentsUploadedForAddMessage>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [isMessageSent, setIsMessageSent] = useState(false);
    const [isQuickResponseLoadedFirstTime, setQuickResponseIsloadedFirstTime] = useState(true);
    const [isMessageInputEmpty, setIsMessageInputEmpty] = useState(true);
    const [selectedQuickResponseID, setSelectedQuickResponseID] = useState('');

    // Callback to track when drafts are saved to localStorage
    const handleDraftSaved = useCallback(() => {
      emitStoreMessageToLocalStorageEvent(user, triggerLocation);
    }, [user]);

    const { clearMessageDrafts, getHasLocalStorageMessageDraft } = useMessageDrafts<
      AttachmentsUploadedForAddMessage[0]
    >({
      conversationId: conversationId || '',
      logger,
      messageInputText: inputText,
      setMessageInputText: setInputText,
      uploadedAttachments,
      setUploadedAttachments,
      onDraftSaved: handleDraftSaved,
    });

    useEffect(() => {
      /**
       * Revoke generated preview urls from AttachmentsUploader to
       * prevent memory leaks, will run on unmount
       * https://developer.mozilla.org/en-US/docs/Web/API/URL/revokeObjectURL
       */
      return () =>
        uploadedAttachments.forEach((uploadedAttachment) => {
          if (uploadedAttachment.generatedPreviewUrl) {
            URL.revokeObjectURL(uploadedAttachment.generatedPreviewUrl);
          }
        });
    }, []);

    const disableComponent = Boolean(isPhoneLead || isImpersonator || isSpam || isConversationBlockedByZillow);
    const hasNoMessageText = formValidationUtils.isInputEmpty(inputText);
    const isAddMessageInputEmpty = hasNoMessageText && uploadedAttachments.length === 0;
    const isSendButtonDisabled = disableComponent || isAddMessageInputEmpty;
    const [shouldFocusAddMessage, setShouldFocusAddMessage] = useState(
      inboxPageIsAddMessageInputFocused && !inboxPageIsConversationSearchBarFocused && !disableComponent,
    );

    useEffect(() => {
      setIsMessageSent(false);
    }, [hasQuickResponses, fullMessageCountNumber]);

    useEffect(() => {
      inboxPageIsAddMessageInputFocusedVariable(true); // Focus input when attachments are added
    }, [uploadedAttachments]);

    /**
     * If inboxPageAddMessageInputTextToAppend has content, append it to current text
     * then reset inboxPageAddMessageInputTextToAppend to ''
     */
    useEffect(() => {
      if (inboxPageAddMessageInputTextToAppend) {
        const messageInput = inputText
          ? `${inputText} ${inboxPageAddMessageInputTextToAppend}`
          : inboxPageAddMessageInputTextToAppend;
        setInputText(messageInput);
        if (hasQuickResponses) {
          setSelectedQuickResponseID('');
          setIsTyping(true);
        }
        inboxPageAddMessageInputTextToAppendVariable('');
      }
    }, [inboxPageAddMessageInputTextToAppend, inputText, hasQuickResponses]);

    /**
     * If inboxPageClearAndAppendMessageInputText has content, clear the current text in the message input box
     * and append the content to current text. Then reset inboxPageClearAndAppendMessageInputText to ''
     */
    useEffect(() => {
      if (inboxPageClearAndAppendMessageInputText) {
        setInputText(inboxPageClearAndAppendMessageInputText);
        setIsMessageInputEmpty(false);
        inboxPageClearAndAppendMessageInputTextVariable('');
      }
    }, [inboxPageClearAndAppendMessageInputText]);

    const { setActiveModal, activeModal, clearModal } = useModal();
    const attachmentsUploaderIsOpened = ModalType.AttachmentsUploader === activeModal;
    const personalContactDetailsWarningModalIsOpened = ModalType.PersonalContactDetailsWarningModal === activeModal;

    /**
     * Allows us to persist attachment data until it is returned from the
     * next getAllForConversation poll in MessageList
     */
    let attachmentsForMessage: AttachmentsForMessage = [];
    const [addMessageMutation] = useAddMessage_AddMessageToConversationV2Mutation({
      update(cache, { data: addMessageResponse }) {
        setCurrentTempMessageId(String(Date.now()));

        const messageText = addMessageResponse?.ZRM_addMessageToConversationV2.message || '';
        const newMessage = {
          ...addMessageResponse?.ZRM_addMessageToConversationV2,
          attachments: attachmentsForMessage,
          messagePreview: getOptimisticMessagePreview({
            attachments: attachmentsForMessage,
            message: messageText,
          }),
        };

        const messageListData = cache.readQuery<
          MessageList_GetConversationQuery,
          MessageList_GetConversationQueryVariables
        >({
          query: MessageList_GetConversationDocument,
          variables: {
            conversationId: conversationId!,
            listingAlias: listingAlias!,
            clientTimeZone,
          },
        });

        if (messageListData?.ZRM_getConversationById) {
          const currentMessages = messageListData.ZRM_getConversationById.conversation || [];
          const failedMessages: MessageList_GetConversationQuery['ZRM_getConversationById']['conversation'] = [];
          const sentMessages = currentMessages.filter((message) => {
            const isFailed = message.messageId?.includes(MESSAGE_STATUS.FAILED);
            if (isFailed) {
              failedMessages.push(message);
            }
            return !isFailed;
          });

          const newConversation = [...sentMessages, newMessage, ...failedMessages];
          cache.writeQuery({
            query: MessageList_GetConversationDocument,
            variables: {
              conversationId: conversationId || '',
              listingAlias: listingAlias || '',
              clientTimeZone,
            },
            data: {
              ZRM_getConversationById: {
                ...messageListData.ZRM_getConversationById,
                conversation: newConversation,
              },
            },
          });
        }

        onAddMessageCompletedCacheUpdate?.({ cache, newMessage, attachmentsForMessage });
      },
    });

    const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputText(e.target.value);
      if (hasQuickResponses) {
        setQuickResponseIsloadedFirstTime(false);
        setSelectedQuickResponseID('');
      }
      if (e.target.value === '') {
        if (hasQuickResponses) {
          setIsTyping(false);
        }
        setIsMessageInputEmpty(true);
      } else {
        if (hasQuickResponses) {
          setIsTyping(true);
        }
        setIsMessageInputEmpty(false);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent): void => {
      if (e.key === 'Enter' && !e.shiftKey && !isSendButtonDisabled && !isMobileDevice) {
        e.preventDefault();
        sendMessage();
      }
    };

    /**
     * Add Message to conversation flow
     * 1) Check for personal contact details and show warning modal if detected
     * 2) Add optimistic message to the conversation in the cache
     * 3) Kick off add message mutation to graphql server
     * 4a) If success, remove optimistic message from cache
     * 4b) If failure, change optimistic message type from 'sending' to 'failed' to change UI
     */
    const sendMessage = (bypassWarning = false) => {
      if (isAddMessageInputEmpty) {
        return;
      }

      // Check for personal contact details unless bypassing the warning
      if (!bypassWarning && hasPersonalContactDetails(inputText)) {
        setActiveModal(ModalType.PersonalContactDetailsWarningModal);
        emitOpenPersonalInfoWarningModalEvent(triggerLocation);
        return;
      }

      if (hasQuickResponses) {
        setIsMessageSent(true);
      }

      const messageText = inputText;
      const { attachmentsForOptimisticMessage = [], zDocIds = [] } = uploadedAttachments.length
        ? getAttachmentDataForAddMessageMutation(uploadedAttachments)
        : {};
      attachmentsForMessage = attachmentsForOptimisticMessage;

      const hasMessageDraft = getHasLocalStorageMessageDraft();
      if (hasMessageDraft) {
        emitSendDraftMessageEvent(user, { listingAlias }, triggerLocation);
      }

      clearMessageDrafts();

      // Optimistic UI
      const messagePreview = getOptimisticMessagePreview({
        attachments: attachmentsForMessage,
        message: messageText,
      });
      setCurrentTempMessageId(String(Date.now()));
      const optimisticMessageId = `temp-${currentTempMessageId}`;
      const normalizedMessageId = apolloClient.cache.identify({
        __typename: 'ZRM_Message',
        conversationId,
        messageId: optimisticMessageId,
      });
      const optimisticMessage = getOptimisticMessageObject({
        conversationId: conversationId || '',
        message: messageText,
        messagePreview,
        messageDateMs: currentTempMessageId,
        messageId: optimisticMessageId,
        attachments: attachmentsForOptimisticMessage,
      });

      const messageListData = apolloClient.cache.readQuery<
        MessageList_GetConversationQuery,
        MessageList_GetConversationQueryVariables
      >({
        query: MessageList_GetConversationDocument,
        variables: {
          conversationId: conversationId || '',
          listingAlias: listingAlias || '',
          clientTimeZone,
        },
      });

      if (messageListData?.ZRM_getConversationById) {
        const currentMessages = messageListData.ZRM_getConversationById.conversation || [];
        const tempMessages: MessageList_GetConversationQuery['ZRM_getConversationById']['conversation'] = [];
        const sentMessages = currentMessages.filter((message) => {
          const isTemp = message.uiTreatmentType?.includes('-');

          if (isTemp) {
            tempMessages.push(message);
          }
          return !isTemp;
        });

        apolloClient.cache.writeQuery({
          query: MessageList_GetConversationDocument,
          variables: {
            conversationId: conversationId || '',
            listingAlias: listingAlias || '',
            clientTimeZone,
          },
          data: {
            ZRM_getConversationById: {
              ...messageListData.ZRM_getConversationById,
              conversation: [...sentMessages, ...tempMessages, optimisticMessage],
            },
          },
        });
      }

      const variables: MutationZrm_AddMessageToConversationV2Args = {
        conversationId: conversationId!,
        listingAlias: listingAlias,
        message: messageText,
        zDocIds,
      };

      if (hasQuickResponses && selectedQuickResponseID) {
        variables.quickResponseSuggestionId = selectedQuickResponseID;
      }

      addMessageMutation({
        variables: variables,
        onCompleted() {
          apolloClient.cache.evict({ id: normalizedMessageId });
          apolloClient.cache.gc();

          emitSendMessageEvent(user, { listingAlias }, triggerLocation);

          if (hasQuickResponses) {
            setSelectedQuickResponseID('');
            setIsTyping(false);
          }
        },
        onError(error) {
          // TODO RRMP-5361: after 2.0: Fix UI of telling user their message is too long
          logApolloError(error, {
            details: 'AddMessage - Add message failed. User seeing failed message treatment',
            conversationId,
            listingAlias,
            messageText,
          });

          // check error here
          apolloClient.cache.writeFragment({
            id: normalizedMessageId,
            fragment: gql`
              fragment OptimisticMessage on ZRM_Message {
                uiTreatmentType
              }
            `,
            data: {
              uiTreatmentType: 'lowPriority-failed',
            },
          });
        },
      });
    };

    const handleQuickResponeSelect = (selectedQuickResponseValue: string | null | undefined) => {
      setSelectedQuickResponseID(selectedQuickResponseValue || '');
    };

    // Keep the draft preserved by not clearing the input
    const handlePersonalContactWarningGoBack = () => {
      clearModal();
    };

    const handlePersonalContactWarningSendAnyway = () => {
      clearModal();
      emitSendAnywayInPersonalInfoWarningModalEvent(triggerLocation);
      sendMessage(true);
    };

    const handleFocus = () => {
      inboxPageIsAddMessageInputFocusedVariable(true);
      setShouldFocusAddMessage(true);
    };

    const handleBlur = () => {
      inboxPageIsAddMessageInputFocusedVariable(false);
      setShouldFocusAddMessage(false);
    };

    const handleRemoveUploadedAttachment = useCallback(
      (zDocId: string) => {
        const filteredUploads = uploadedAttachments.filter((attachment) => attachment.zDocId !== zDocId);
        setUploadedAttachments(filteredUploads);
      },
      [uploadedAttachments],
    );

    const isAddAttachmentsDisabled = Boolean(isPhoneLead || isSpam || isConversationBlockedByZillow);
    const tooltipText = useMemo(() => {
      if (isPhoneLead) {
        return StringConstants.TOOLTIP.PHONE_LEAD;
      }
      if (isImpersonator) {
        return StringConstants.TOOLTIP.IMPERSONATION;
      }
      if (isConversationBlockedByZillow) {
        return StringConstants.TOOLTIP.BLOCKED_BY_ZILLOW;
      }
      if (isSpam) {
        return StringConstants.TOOLTIP.REPORTED;
      }
      return '';
    }, [isSpam, isPhoneLead, isImpersonator, isConversationBlockedByZillow]);

    const containsUploadedAttachments = !!uploadedAttachments.length;
    const displayQuickResponseCTAs =
      enableQuickResponse &&
      hasQuickResponses &&
      !isTyping &&
      !attachmentsUploaderIsOpened &&
      !containsUploadedAttachments &&
      !isMessageSent;

    const placeholderText =
      isMobileDevice || isCompact ? 'Write message...' : 'Type a message… (use Shift + Enter for a new line)';

    // ── Shared sub-elements used by both variants ─────────────────────────

    const textareaElement = (
      <TextareaAutoSize
        ariaLabel="Message input"
        isDisabled={disableComponent}
        onBlur={handleBlur}
        onChange={handleTextAreaChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholderText}
        rows={1}
        shouldFocus={shouldFocusAddMessage}
        value={inputText}
      />
    );

    const attachmentsButton = (
      <AddAttachments
        bordered={isCompact}
        guidelinesVariant={isCompact ? 'compact' : 'default'}
        isDisabled={isAddAttachmentsDisabled}
        setIsAttachmentsUploaderOpen={() => setActiveModal(ModalType.AttachmentsUploader)}
      />
    );

    const attachmentsList = !!uploadedAttachments.length && (
      <UploadedAttachmentList
        uploadedAttachments={uploadedAttachments}
        handleRemoveUploadedAttachment={handleRemoveUploadedAttachment}
        triggerLocation={triggerLocation}
      />
    );

    const quickResponseElement = (
      <QuickResponse
        quickResponses={quickResponses}
        isQuickResponseLoadedFirstTime={isQuickResponseLoadedFirstTime}
        isMessageInputEmpty={isMessageInputEmpty}
        onQuickResponseSelect={handleQuickResponeSelect}
        triggerLocation={triggerLocation}
      />
    );

    const compactAddMessageContent = (
      <Box
        css={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <TooltipWrapper wrapInTooltip={disableComponent} tooltipText={tooltipText}>
          <Box css={{ width: '100%', '& textarea': { lg: { boxShadowColor: 'border.neutral' } } }}>
            <CompactAddMessageLayout
              ref={ref}
              isSendDisabled={isSendButtonDisabled}
              onSend={sendMessage}
              leftButtons={attachmentsButton}
              sendMessageDataEventId={SEND_MESSAGE_EVENT_TYPE_ID}
              bottomContent={
                attachmentsList ? (
                  <Box css={{ '& > div': { marginTop: 'tighter', paddingBottom: 0 } }}>{attachmentsList}</Box>
                ) : undefined
              }
            >
              {textareaElement}
            </CompactAddMessageLayout>
          </Box>
        </TooltipWrapper>
        {displayQuickResponseCTAs && (
          <Box css={{ flexShrink: 0, marginTop: 'default', marginInline: '-default', marginBottom: '-default' }}>
            {quickResponseElement}
          </Box>
        )}
      </Box>
    );

    const defaultAddMessageContent = (
      <>
        <MessageListActions
          conversationId={conversationId}
          listingAlias={listingAlias}
          clientTimeZone={clientTimeZone}
          variant={variant}
          triggerLocation={triggerLocation}
        />
        <TooltipWrapper wrapInTooltip={disableComponent} tooltipText={tooltipText}>
          <AddMessageLayout
            ref={ref}
            isSendDisabled={isSendButtonDisabled}
            onSend={sendMessage}
            leftButtons={attachmentsButton}
            sendMessageDataEventId={SEND_MESSAGE_EVENT_TYPE_ID}
          >
            {textareaElement}
            {attachmentsList}
          </AddMessageLayout>
        </TooltipWrapper>
        {displayQuickResponseCTAs && quickResponseElement}
      </>
    );

    return (
      <>
        {attachmentsUploaderIsOpened && (
          <AttachmentsUploader
            variant={isCompact ? 'compact' : 'default'}
            existingUploadedAttachments={uploadedAttachments}
            setAttachmentsUploadedForAddMessage={setUploadedAttachments}
          />
        )}

        {personalContactDetailsWarningModalIsOpened && (
          <PersonalContactDetailsWarningModal
            warningData={personalContactWarningData}
            onGoBackClick={handlePersonalContactWarningGoBack}
            onSendAnywayClick={handlePersonalContactWarningSendAnyway}
            fallbackLinkUrl={Urls.PERSONAL_CONTACT_WARNING_SCAM_PREVENTION}
          />
        )}

        {isCompact ? compactAddMessageContent : defaultAddMessageContent}
      </>
    );
  },
);

AddMessage.displayName = 'AddMessage';
export default AddMessage;

const GET_ADD_MESSAGE_INPUT_TEXT = gql`
  query {
    inboxPageAddMessageInputTextToAppend @client
    inboxPageIsAddMessageInputFocused @client
    inboxPageIsConversationSearchBarFocused @client
    inboxPageClearAndAppendMessageInputText @client
  }
`;

const getOptimisticMessageObject = ({
  conversationId,
  messageDateMs,
  message,
  messagePreview,
  messageId,
  attachments,
}: {
  conversationId: string;
  messageDateMs: string;
  message: string;
  messagePreview: string;
  messageId: string;
  attachments: AttachmentsForMessage;
}) => ({
  __typename: 'ZRM_Message',
  aiAutoResponseType: null,
  conversationId,
  messageDateMs,
  messageType: 'listingMessage',
  message,
  messagePreview,
  senderName: '',
  uiTreatmentType: 'lowPriority-sending',
  attachments,
  isMessageOwner: true,
  messageLink: {
    url: '',
    text: '',
    __typename: 'ZRM_CTA_Link',
  },
  messageId,
  title: '',
});

const getOptimisticMessagePreview = ({
  attachments,
  message,
}: {
  attachments: AttachmentsForMessage;
  message: string;
}) => {
  const shouldShowAttachmentTextPreview = attachments.length && !message;
  const attachmentTextPreview = `${attachments.length} ${stringUtils.pluralize('attachment', attachments.length)}`;
  return shouldShowAttachmentTextPreview ? attachmentTextPreview : message;
};

const getAttachmentDataForAddMessageMutation = (uploadedAttachments: AttachmentsUploadedForAddMessage) => {
  const zDocIds: Array<string> = [];
  const attachmentsForOptimisticMessage = uploadedAttachments.map((uploadedAttachment) => {
    const { file, generatedPreviewUrl, zDocId } = uploadedAttachment;
    const attachmentAdded = new Date().getTime();

    if (zDocId) {
      zDocIds.push(zDocId);
    }

    return {
      __typename: 'ZRM_Attachment',
      attachmentAddedMs: attachmentAdded.toString(),
      attachmentMetadataStatus: 'complete',
      downloadUrl: '',
      fileName: file.name,
      fileSizeBytes: file.size,
      fileType: file.type,
      isAttachmentDeleted: false,
      isAttachmentExpired: false,
      previewUrl: generatedPreviewUrl,
      zDocId,
    } as Zrm_Attachment;
  });

  return {
    attachmentsForOptimisticMessage,
    zDocIds,
  };
};
